/**
 * Simple toast notification system to replace Materialize toasts
 * Maintains the same API and behavior as the original messageDispatchHelper
 *
 * Features:
 * - Plain text message support (backward compatible)
 * - HTML content rendering with XSS protection
 * - Safe HTML tags: <strong>, <em>, <br>, <a> (with security attributes)
 * - Automatic sanitization of malicious content
 */

interface ToastOptions {
  message: string;
  level: "INFO" | "WARN" | "ERROR";
  duration?: number;
}

/**
 * Basic HTML sanitization to prevent XSS attacks
 * Only allows safe HTML tags and attributes commonly used in toast messages
 */
function sanitizeHtml(html: string): string {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Remove dangerous elements
  const dangerousElements = tempDiv.querySelectorAll('script, style, iframe, object, embed, form, input, button');
  dangerousElements.forEach(el => el.remove());

  // Remove dangerous attributes from all elements
  const allElements = tempDiv.querySelectorAll('*');
  allElements.forEach(el => {
    const attrs = Array.from(el.attributes);
    attrs.forEach(attr => {
      // Remove event handlers and potentially dangerous attributes
      if (attr.name.startsWith('on') ||
          ['href', 'src', 'action', 'formaction'].includes(attr.name) && !isSafeUrl(attr.value)) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return tempDiv.innerHTML;
}

/**
 * Check if a URL is safe (only allow http/https links)
 */
function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

let toastContainer: HTMLElement | null = null;

function ensureToastContainer(): HTMLElement {
  if (!toastContainer) {
    toastContainer = document.createElement("div");
    toastContainer.id = "e4s-toast-container";
    toastContainer.className = "e4s-toast-container";
    document.body.appendChild(toastContainer);
  }
  return toastContainer;
}

function createToastElement(options: ToastOptions): HTMLElement {
  const toast = document.createElement("div");
  toast.className = `e4s-toast e4s-toast-${options.level.toLowerCase()}`;

  // Create message span with HTML support
  const messageSpan = document.createElement("span");
  // Use innerHTML with sanitization to support HTML content while preventing XSS
  messageSpan.innerHTML = sanitizeHtml(options.message);

  // Create close button
  const closeButton = document.createElement("span");
  closeButton.className = "e4s-toast-close";
  closeButton.innerHTML = "&times;";
  closeButton.addEventListener("click", () => removeToast(toast));

  // Append elements
  toast.appendChild(messageSpan);
  toast.appendChild(closeButton);

  return toast;
}

function removeToast(toast: HTMLElement): void {
  toast.classList.add("e4s-toast-hide");
  setTimeout(() => {
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast);
    }
  }, 300); // Wait for fade-out animation
}

export function showToast(options: ToastOptions): void {
  try {
    const container = ensureToastContainer();
    const toast = createToastElement(options);

    // Add to container
    container.appendChild(toast);

    // Trigger animation
    setTimeout(() => toast.classList.add("e4s-toast-show"), 10);

    // Auto-remove after duration
    const duration = options.duration || 5000;
    setTimeout(() => removeToast(toast), duration);
  } catch (error) {
    // Fallback to console if toast creation fails
    console.warn("Toast message (fallback):", options.message);
  }
}
